<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Decimal } from 'decimal.js'
import { useRegistry } from '@hantera/portal-app'
import { DecimalInput, DecimalInputValue, DropdownButton, DropdownItem } from '@hantera/design-system'

const registry = useRegistry()

const props = defineProps<{
  modelValue: Record<string, Decimal>
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, Decimal>): void
}>()

interface CurrencyInfo {
  code: string
  label: string
}

const currencies = ref<CurrencyInfo[]>([])

onMounted(async () => {
  try {
    const data = await registry.list('currencies', true)
    currencies.value = Object.entries(data).map(([fullKey, value]) => {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value
      return {
        code: fullKey.split('/').pop()!,
        label: parsed?.label || '',
      }
    }).sort((a, b) => a.code.localeCompare(b.code))
  } catch {
    currencies.value = []
  }
})

const orderedEntries = computed(() => {
  const entries = Object.keys(props.modelValue ?? {})
  const known = currencies.value.map(c => c.code)
  return [
    ...known.filter(c => entries.includes(c)),
    ...entries.filter(c => !known.includes(c)).sort()
  ]
})

const availableToAdd = computed(() => {
  const present = new Set(Object.keys(props.modelValue ?? {}))
  return currencies.value.filter(c => !present.has(c.code))
})

function getLabel(code: string): string {
  return currencies.value.find(c => c.code === code)?.label || code
}

function updateValue(code: string, value: Decimal) {
  const next = { ...(props.modelValue ?? {}) }
  next[code] = value
  emit('update:modelValue', next)
}

function removeCurrency(code: string) {
  const next = { ...(props.modelValue ?? {}) }
  delete next[code]
  emit('update:modelValue', next)
}

function addCurrency(code: string) {
  if (props.modelValue?.[code]) return
  const next = { ...(props.modelValue ?? {}) }
  next[code] = new Decimal(0)
  emit('update:modelValue', next)
}
</script>

<template>
  <div class="currency-input">
    <div
      v-for="code in orderedEntries"
      :key="code"
      class="currency-input__row"
    >
      <span class="currency-input__code" :title="getLabel(code)">{{ code }}</span>
      <DecimalInput
        :model-value="modelValue[code]"
        @update:model-value="(v: Decimal | DecimalInputValue | undefined) => updateValue(code, v as Decimal)"
        :disabled="disabled"
      />
      <button
        v-if="!disabled"
        class="btn-silent currency-input__remove"
        @click="removeCurrency(code)"
        title="Remove currency"
        type="button"
      >✕</button>
    </div>

    <div v-if="!disabled && availableToAdd.length > 0" class="currency-input__add">
      <DropdownButton label="+ Add currency" button-style="silent">
        <DropdownItem
          v-for="c in availableToAdd"
          :key="c.code"
          @click="addCurrency(c.code)"
        >
          <span class="currency-input__add-code">{{ c.code }}</span>
          <span class="currency-input__add-label">{{ c.label }}</span>
        </DropdownItem>
      </DropdownButton>
    </div>
  </div>
</template>

<style scoped>
.currency-input {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.currency-input__row {
  display: grid;
  grid-template-columns: 60px 1fr auto;
  align-items: center;
  gap: 8px;
}

.currency-input__code {
  font-size: 0.8rem;
  font-family: monospace;
  color: hsl(var(--color-40));
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-input__remove {
  width: 24px;
  height: 24px;
  font-size: 14px;
  padding: 0;
}

.currency-input__add {
  margin-top: 2px;
}

.currency-input__add-code {
  font-family: monospace;
  font-weight: 600;
  margin-right: 8px;
}

.currency-input__add-label {
  color: hsl(var(--color-40));
}
</style>
